/**
 * RpgBattlePage (ТЗ №5) — the mob roster and battle flow.
 *
 * Lists mobs from ``GET /api/rpg/mobs/`` with their icon / level / hp / damage / win
 * chance / reward / required rank; unavailable mobs show the server's lock reason (rank,
 * daily limit or cooldown). A fight posts to ``POST /api/rpg/battle/`` and shows the
 * outcome (win/loss, rewards, drops, level-up) in a sheet. Anti-double-click: the whole
 * roster is disabled while a battle is in flight and per-mob while that mob is locked.
 */
import { useState } from "react";

import { CurrencyIcon, Sheet } from "@melusine/shared-ui";
import { useBackButton } from "@melusine/shared-telegram";
import {
  canBattle,
  formatCooldown,
  hpPercent,
  insufficientHpLabel,
  mobLockLabel,
  useRpgMobs,
  useRpgProfile,
  winChancePercent,
  type RpgBattleResult,
  type RpgMob,
  type RpgProfile,
} from "@entities/rpg";
import { rpgErrorMessage, useBattleMutation } from "@features/rpg";
import { StatePanel } from "@widgets/catalog-browser";
import { useToast } from "@shared/ui/toast";
import { formatUnits } from "@shared/lib/format";
import { routes, useNavigate } from "@shared/router";

import { RpgHeader } from "./ui/RpgHeader";
import { RpgIcon } from "./ui/RpgIcon";
import styles from "./rpg.module.css";

export function RpgBattlePage(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const mobsQuery = useRpgMobs();
  const profileQuery = useRpgProfile();
  const battle = useBattleMutation();
  const [result, setResult] = useState<RpgBattleResult | null>(null);

  useBackButton(() => {
    navigate(routes.rpg());
  });

  const profile = profileQuery.data;
  const hpBlocked = profile !== null && !canBattle(profile);

  const onFight = async (mob: RpgMob): Promise<void> => {
    if (battle.isLoading) {
      return;
    }
    // Pre-emptive client-side block so an out-of-HP hero can't spam the endpoint;
    // the server re-checks and raises InsufficientHp regardless.
    if (profile !== null && !canBattle(profile)) {
      toast.show(insufficientHpLabel(profile), "default");
      return;
    }
    try {
      const res = await battle.mutate(mob.id);
      setResult(res);
      mobsQuery.refetch();
      profileQuery.refetch();
    } catch (err) {
      toast.show(rpgErrorMessage(err), "default");
    }
  };

  if (mobsQuery.isLoading) {
    return <StatePanel kind="loading" />;
  }
  if (mobsQuery.error && mobsQuery.data === null) {
    return (
      <StatePanel
        kind="error"
        message="Не удалось загрузить противников."
        onRetry={mobsQuery.refetch}
      />
    );
  }

  const mobs = mobsQuery.data ?? [];

  return (
    <div className={styles.page}>
      <RpgHeader
        title="Бой"
        onBack={() => navigate(routes.rpg())}
        {...(profile ? { mora: profile.mora } : {})}
      />

      {profile ? <BattleHpCard profile={profile} /> : null}

      {mobs.length === 0 ? (
        <StatePanel kind="empty" message="Противники пока недоступны." />
      ) : (
        <div className={styles.list}>
          {mobs.map((mob) => (
            <MobRow
              key={mob.id}
              mob={mob}
              busy={battle.isLoading}
              hpBlocked={hpBlocked}
              hpHint={profile ? insufficientHpLabel(profile) : ""}
              onFight={() => void onFight(mob)}
            />
          ))}
        </div>
      )}

      <Sheet
        open={result !== null}
        onClose={() => setResult(null)}
        title={result?.won ? "Победа" : "Поражение"}
      >
        {result ? (
          <BattleResultView result={result} onClose={() => setResult(null)} />
        ) : null}
      </Sheet>
    </div>
  );
}

function BattleHpCard({
  profile,
}: {
  readonly profile: RpgProfile;
}): JSX.Element {
  const blocked = !canBattle(profile);
  return (
    <section className={styles.hpCard}>
      <div className={styles.xpHead}>
        <span className={styles.xpLabel}>Здоровье</span>
        <span className={styles.xpValue}>
          {formatUnits(profile.hp)} / {formatUnits(profile.max_hp)}
        </span>
      </div>
      <div className={styles.xpTrack}>
        <div
          className={styles.hpFill}
          style={{ width: `${hpPercent(profile)}%` }}
        />
      </div>
      <div className={styles.hpMeta}>
        <span>Бой: −{formatUnits(profile.battle_hp_cost)} HP</span>
        <span>Реген: +{profile.hp_regen_per_minute}/мин</span>
        {profile.hp < profile.max_hp && profile.seconds_to_next_hp > 0 ? (
          <span>+1 HP через {formatCooldown(profile.seconds_to_next_hp)}</span>
        ) : null}
      </div>
      <div className={styles.hpMeta}>
        <span>
          Бои сегодня: {profile.battle_quota.used}/{profile.battle_quota.limit}
        </span>
        <span>Осталось: {profile.battle_quota.remaining}</span>
      </div>
      {blocked ? (
        <div className={styles.hpWarn}>{insufficientHpLabel(profile)}</div>
      ) : null}
    </section>
  );
}

function MobRow({
  mob,
  busy,
  hpBlocked,
  hpHint,
  onFight,
}: {
  readonly mob: RpgMob;
  readonly busy: boolean;
  readonly hpBlocked: boolean;
  readonly hpHint: string;
  readonly onFight: () => void;
}): JSX.Element {
  const locked = !mob.available;
  // HP shortage blocks every mob (but the mob's own lock reason takes precedence).
  const hpOnlyBlock = !locked && hpBlocked;
  const lockLabel = mobLockLabel(mob) ?? (hpOnlyBlock ? hpHint : null);
  return (
    <div
      className={`${mob.is_boss ? styles.rowBoss : styles.row} ${
        locked || hpOnlyBlock ? styles.rowLocked : ""
      }`}
    >
      <RpgIcon url={mob.icon_url} name={mob.name} size={48} />
      <div className={styles.rowBody}>
        <div className={styles.rowNameRow}>
          <span className={styles.rowName}>{mob.name}</span>
          {mob.is_boss ? <span className={styles.bossTag}>Босс</span> : null}
          <span className={styles.tag}>Ур. {mob.level}</span>
        </div>
        <div className={styles.metaLine}>
          <span>HP {formatUnits(mob.hp)}</span>
          <span>Урон {formatUnits(mob.attack)}</span>
          <span>Шанс {winChancePercent(mob.win_chance)}%</span>
          {mob.daily_limit > 0 ? (
            <span>
              Сегодня {mob.fought_today}/{mob.daily_limit}
            </span>
          ) : null}
        </div>
        <div className={styles.reward}>
          +{formatUnits(mob.reward_mora)} моры · +{formatUnits(mob.reward_xp)} XP
        </div>
        {lockLabel ? (
          <div className={styles.lockReason}>{lockLabel}</div>
        ) : null}
      </div>
      <button
        type="button"
        className={`${styles.actionBtn} ${mob.is_boss ? styles.actionBtnGlitter : ""}`}
        disabled={locked || busy || hpOnlyBlock}
        onClick={onFight}
      >
        В бой
      </button>
    </div>
  );
}

function BattleResultView({
  result,
  onClose,
}: {
  readonly result: RpgBattleResult;
  readonly onClose: () => void;
}): JSX.Element {
  return (
    <div className={styles.battleResult}>
      <div
        className={`${styles.resultTitle} ${
          result.won ? styles.resultWin : styles.resultLoss
        }`}
      >
        {result.won ? "Победа!" : "Поражение"}
      </div>

      {result.won ? (
        <div className={styles.resultRewards}>
          <div className={styles.resultLine}>
            <CurrencyIcon kind="mora" size={16} />+
            {formatUnits(result.mora_reward)} моры
          </div>
          <div className={styles.resultLine}>
            +{formatUnits(result.xp_reward)} опыта
          </div>
          {result.drops.length > 0 ? (
            <div className={styles.resultDrops}>
              {result.drops.map((drop) => (
                <span key={drop.item_id} className={styles.dropChip}>
                  {drop.name} ×{drop.qty}
                </span>
              ))}
            </div>
          ) : null}
          {result.leveled_up ? (
            <div className={styles.levelUp}>
              Новый уровень {result.new_level}! Ранг {result.new_rank}
            </div>
          ) : null}
        </div>
      ) : (
        <div className={styles.muted}>
          В этот раз не повезло. Прокачайтесь и попробуйте снова.
        </div>
      )}

      <button type="button" className={styles.primaryBtn} onClick={onClose}>
        Продолжить
      </button>
    </div>
  );
}
